/*
  openerMessaging.js  (hosted payment page, src/utils/openerMessaging.js)
  -----------------------------------------------------------------------
  Messages to the page that opened checkout as a popup (window.open), e.g. a
  merchant site or the dashboard top-up.

  postMessage(msg, "*") hands the message to whatever origin the opener
  happens to be on at that moment, including one it navigated to after
  opening us (SonarQube javascript:S2819). So we pin the target origin:

    1. `openerOrigin` query param, if the integration passes one (most
       reliable; nothing is inferred), else
    2. the origin of document.referrer on the popup's FIRST load, which is
       the opener page (the browser keeps the origin under the default
       referrer policy).

  It's captured once at startup (index.js) and kept in sessionStorage,
  because document.referrer changes after any full-page redirect (3DS bank
  pages, etc.).

  If no origin is known, we don't send. A message aimed at an unknown origin
  is exactly what the rule exists to prevent; the popup still closes.
*/

const OPENER_ORIGIN_KEY = "wq_openerOrigin";

const toOrigin = (url) => {
  if (!url) return null;
  try {
    const { origin } = new URL(url);
    return origin && origin !== "null" ? origin : null;
  } catch (e) {
    return null;
  }
};

export const getOpenerOrigin = () => {
  try {
    return sessionStorage.getItem(OPENER_ORIGIN_KEY);
  } catch (e) {
    return null;
  }
};

// Call once, as early as possible (index.js), before any redirect changes
// document.referrer. Keeps the first value it sees for this tab.
export const captureOpenerOrigin = () => {
  if (!window.opener) return;
  if (getOpenerOrigin()) return;

  const params = new URLSearchParams(window.location.search);
  const origin = toOrigin(params.get("openerOrigin")) || toOrigin(document.referrer);
  if (!origin) return;

  try {
    sessionStorage.setItem(OPENER_ORIGIN_KEY, origin);
  } catch (e) {
    // storage blocked: notifyOpener will skip sending
  }
};

/*
  Post a message to the opener, pinned to its origin. Pass targetOrigin
  explicitly when sessionStorage is about to be (or has been) cleared, since
  the origin lives there. Returns true if a message was sent.
*/
export const notifyOpener = (message, targetOrigin = getOpenerOrigin()) => {
  if (!window.opener || window.opener.closed) return false;

  if (!targetOrigin) {
    console.warn("[wayaquick-checkout] opener origin unknown; message not sent:", message?.type);
    return false;
  }

  try {
    window.opener.postMessage(message, targetOrigin);
    return true;
  } catch (e) {
    return false;
  }
};
